const ID_ALPHABET = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';

// Declared locally so this package needs neither DOM nor Node typings.
declare const crypto: { getRandomValues<T extends Uint8Array>(array: T): T };
declare const URL: new (input: string) => { href: string; hostname: string };

/** Random URL-safe id. Works in browsers and Node (both expose Web Crypto). */
export function newId(size = 14): string {
  const bytes = new Uint8Array(size);
  crypto.getRandomValues(bytes);
  let id = '';
  for (const byte of bytes) id += ID_ALPHABET[byte % ID_ALPHABET.length];
  return id;
}

export const ID_PATTERN = /^[0-9A-Za-z]{8,32}$/;

export function slugify(input: string): string {
  const slug = input
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80)
    .replace(/-+$/, '');
  // "edit" would clash with the /recipes/:id/edit route.
  return !slug || slug === 'edit' ? 'recipe' : slug;
}

/**
 * The web address in what someone typed, pasted or shared ("Look at this: https://…"),
 * with https:// added when it was left out. Null when there isn't one.
 */
export function findUrl(text: string): string | null {
  const found = /https?:\/\/[^\s<>"']+/i.exec(text)?.[0] ?? /(?:^|\s)((?:[a-z0-9-]+\.)+[a-z]{2,}(?:[/?#][^\s<>"']*)?)/i.exec(text)?.[1];
  if (!found) return null;
  // A sentence can end right after the link.
  const trimmed = found.replace(/[.,;:!?)\]]+$/, '');
  try {
    const url = new URL(/^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`);
    return url.hostname.includes('.') ? url.href : null;
  } catch {
    return null;
  }
}

export const IMAGE_WIDTHS = [400, 800, 1600] as const;
export type ImageWidth = (typeof IMAGE_WIDTHS)[number];

export function imageUrl(id: string, width: ImageWidth = 800): string {
  return `/uploads/${id}/${width}.webp`;
}

export function imageSrcSet(id: string): string {
  return IMAGE_WIDTHS.map((width) => `${imageUrl(id, width)} ${width}w`).join(', ');
}

/** Size limits shared by the editor and the API. */
export const LIMITS = {
  displayName: 50,
  passwordMin: 8,
  passwordMax: 200,
  recipeName: 120,
  description: 20_000,
  steps: 100,
  instruction: 2_000,
  notes: 10_000,
  ingredientsPerStep: 50,
  ingredientName: 120,
  ingredientNote: 200,
  recipeImages: 20,
  stepImages: 6,
  servings: 100,
  totalTimeMinutes: 10_080,
  stepDurationMinutes: 10_080,
  comment: 2_000,
  listName: 60,
  lists: 50,
  imageUploadBytes: 15 * 1024 * 1024,
  /** Photos of one recipe sent to the AI (e.g. both pages of a cookbook spread). */
  aiPhotos: 4,
  /** A description, a question or a change request sent to the AI. */
  aiText: 4_000,
  /** Earlier messages sent along with a question or change request. */
  aiHistory: 8,
  audioUploadBytes: 10 * 1024 * 1024,
  recordingSeconds: 120,
} as const;
